import React, { useEffect, useState } from 'react';
import { useAuth } from '../../AuthContext';
import {
  Box,
  Container,
  Toolbar,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tabs, Tab,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header1 from '../Header/Header1';
import Platform from '../Platform/Platform';
import Footer2 from '../Footer/Footer2';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ShareButton from '../Share/ShareButton';
import { formatDateWithSuffix } from '../utils/formatDate';
import { formatTimeToAMPM } from '../utils/formatTime';
import { formatDuration } from '../utils/formatDuration';
import NeonSpeedometer from "./NeonSpeedometer";


const API_URL = process.env.REACT_APP_API_URL;

const MyPosts = ({ onLogout }) => {
  const { userId } = useAuth();
  const { role, roles } = useAuth();
  const userRoles = Array.isArray(roles) && roles.length > 0 ? roles : [role];
  const { loggedIn } = useAuth();
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('Upcoming');
  const navigate = useNavigate();
  const limit = 10;
  const location = useLocation();
  const [loadingSeconds, setLoadingSeconds] = useState(0);

  const allowedRoles = ['anthra-sales-executive', 'admin', 'expert' , 'user'];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    let timer;

    if (loading) {
      setLoadingSeconds(0); // reset timer
      timer = setInterval(() => {
        setLoadingSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timer);
      setLoadingSeconds(0); // optional reset after done
    }

    return () => clearInterval(timer); // cleanup on unmount or loading = false
  }, [loading]);

  useEffect(() => {
    if (!userId) return;

    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_URL}/list_my_posts_data/?user_id=${userId}&page=${page}&limit=${limit}&status=${filterStatus}`
        );
        if (!res.ok) throw new Error('Failed to fetch sessions.');
        const data = await res.json();
        setPosts(data.posts);
        setTotal(data.total);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [userId, filterStatus, page]);

  const handleFilterChange = (event, newFilter) => {
    if (newFilter && newFilter !== filterStatus) {
      setFilterStatus(newFilter);
      setPage(1);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString();
  const formatTime = (timeStr) => {
    const [h, m] = timeStr.split(':');
    const dt = new Date();
    dt.setHours(h);
    dt.setMinutes(m);
    return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };
  const formatDuration = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h ? `${h}h ` : ''}${m ? `${m}m` : ''}`.trim();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Live':
        return '#4caf50';
      case 'Upcoming':
        return '#2196f3';
      case 'Completed':
        return '#a4ad1bff';
      default:
        return '#757575';
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(total / limit);

  return (
    <>


      <Box
        sx={{
          position: 'fixed', // or 'relative' / 'sticky' based on your layout
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1300, // higher than MUI drawers (default is 1200)
        }}
      >
        <Header1 onLogout={onLogout} />
      </Box>
      <Box>
        <Platform userId={userId} onLogout={onLogout} />
      </Box>

      {loggedIn && (
        <Box position="static" color="primary" sx={{ mt: 7, backgroundColor: '#000000' }}>
          <Toolbar>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 2 }}>

              {/* Single Button */}
              <Button
                variant={isActive('/platform/explore') ? 'contained' : 'outlined'}
                color={isActive('/platform/explore') ? 'secondary' : 'inherit'}
                onClick={() => navigate('/platform/explore')}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1,
                  bgcolor: isActive('/platform/explore') ? 'secondary.main' : 'transparent',
                  color: isActive('/platform/explore') ? 'white' : 'inherit',
                  border: '2px solid',
                  borderColor: isActive('/platform/explore') ? 'primary.main' : 'inherit',
                  '&:hover': {
                    bgcolor: isActive('/platform/explore') ? 'secondary.dark' : 'action.hover',
                    borderColor: 'primary.main',
                  },
                }}
              >
                Explore
              </Button>

              {/* Multiple Button */}
              {allowedRoles.some(r => userRoles.includes(r)) && (
                <Button
                  variant={isActive('/platform/my-hosts') ? 'contained' : 'outlined'}
                  color={isActive('/platform/my-hosts') ? 'secondary' : 'inherit'}
                  onClick={() => navigate('/platform/my-hosts')}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1,
                    bgcolor: isActive('/platform/my-hosts') ? 'secondary.main' : 'transparent',
                    color: isActive('/platform/my-hosts') ? 'white' : 'inherit',
                    border: '2px solid',
                    borderColor: isActive('/platform/my-hosts') ? 'primary.main' : 'inherit',
                    '&:hover': {
                      bgcolor: isActive('/platform/my-hosts') ? 'secondary.dark' : 'action.hover',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  Hostings
                </Button>
              )}

              <Button
                variant={isActive('/platform/bookings') ? 'contained' : 'outlined'}
                color={isActive('/platform/bookings') ? 'secondary' : 'inherit'}
                onClick={() => navigate('/platform/bookings')}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1,
                  bgcolor: isActive('/platform/bookings') ? 'secondary.main' : 'transparent',
                  color: isActive('/platform/bookings') ? 'white' : 'inherit',
                  border: '2px solid',
                  borderColor: isActive('/platform/bookings') ? 'primary.main' : 'inherit',
                  '&:hover': {
                    bgcolor: isActive('/platform/bookings') ? 'secondary.dark' : 'action.hover',
                    borderColor: 'primary.main',
                  },
                }}
              >
                Appointments
              </Button>

            </Box>
          </Toolbar>
        </Box>
      )}

      <Typography
        variant="h3"
        component="h1"
        align="center"
        fontSize={{ xs: '2rem', sm: '2rem', md: '2.5rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
          mt: 5,
          //   mb:3,
          letterSpacing: 7,
          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
          color: 'transparent',  // Makes the text itself transparent to show the gradient
        }}
        gutterBottom
      >
        Your Hosted Sessions
      </Typography>
      <NeonSpeedometer value={posts.length} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',           // Optional: control stacking direction
          justifyContent: 'center',          // Horizontal alignment for row or vertical for column
          alignItems: 'center',              // Vertical alignment for row or horizontal for column
          ml: {
            xs: 0,
            sm: '40px', // adjust based on your sidebar width
          },
          transition: 'margin-left 0.3s ease-in-out',
        }}
      >
        <Container maxWidth="xl" sx={{ mt: 2, mb: 6 }}>
          {/* <Typography
                    variant="h4"
                    align="center"
                    sx={{
                        fontWeight: 'bold',
                        mb: 3,
                        letterSpacing: 1,
                        background: 'linear-gradient(to right, #0f9, #0cf)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Your Hosted Sessions
                </Typography> */}









          <Tabs
            value={filterStatus}
            exclusive
            onChange={handleFilterChange}
            centered
            variant="fullWidth"
            sx={{ my: 3 }}
          >
            <Tab label="Upcoming" value="Upcoming" />
            <Tab label="Live" value="Live" />
            <Tab label="Completed" value="Completed" />
          </Tabs>

          {loading && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(3px)',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress
                  size={60}
                  thickness={6}
                  sx={{
                    color: 'transparent',
                    '& .MuiCircularProgress-circle': {
                      stroke: 'url(#better-gradient)',
                    },
                  }}
                />

                <svg width="0" height="0">
                  <defs>
                    <linearGradient id="better-gradient" x1="1" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f30b96ff" />     {/* pinkish-purple */}
                      <stop offset="50%" stopColor="#221ce0ff" />     {/* deep lavender */}
                      <stop offset="100%" stopColor="#0eacebff" />    {/* bright sky blue */}
                    </linearGradient>
                  </defs>
                </svg>

                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    color: '#fff',
                    textShadow: '0 1px 3px rgba(0,0,0,0.7)',
                  }}
                >
                  Loading Sessions... ({loadingSeconds} sec)
                </Typography>
              </Box>
            </Box>
          )}

          {error && (
            <Typography color="error" align="center" mt={4}>
              {error}
            </Typography>
          )}

          {!loading && Array.isArray(posts) && posts.length === 0 && (
            <Box textAlign="center" mt={6} sx={{ minHeight: '50vh' }}>
              <EventAvailableIcon sx={{ fontSize: 60, color: 'grey.600', mb: 2 }} />
              <Typography variant="h6">No sessions found.</Typography>
              <Typography color="text.secondary">
                Explore the platform for more sessions
              </Typography>
            </Box>
          )}


          <Box
  sx={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    mt: 1,
  }}
>
  {Array.isArray(posts) &&
    posts.map((post) => {
      const status = filterStatus;
      const statusColor = getStatusColor(status);

      return (
        <Box
          key={post.id}
          sx={{
            width: {
              xs: '100%',
              sm: 'calc(50% - 16px)',
              md: 'calc(33.333% - 22px)',
            },
            display: 'flex',
          }}
        >
          <Card
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              borderRadius: 3,
              backgroundColor: '#000000ff',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                backgroundColor: '#111111',
              },
            }}
          >
            {/* Share Button */}
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
              }}
            >
              <ShareButton
                postUrl={`${window.location.origin}/share/${
                  post.is_bundle ? 'bundle' : 'post'
                }/${post.id}`}
              />
            </Box>

            <Link
              to={
                post.is_bundle
                  ? `/my-post-bundle-detail/${post.id}`
                  : `/my-post-tab-details/${post.id}`
              }
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <CardContent>
                {/* Title */}
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 500,
                    mr: 5,
                    fontSize: '1.1rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {post.title}
                </Typography>

                {/* Date / Time */}
                {post.selectedDate && post.startTime && (
                  <Box
                    sx={{
                      mt: { xs: 2, sm: 1 },
                      mb: { xs: 4, sm: 5 },
                    }}
                  >
                    <Typography
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 600,
                        fontSize: {
                          xs: '0.95rem',
                          sm: '1rem',
                          md: '0.9rem',
                        },
                        color: '#ffffff',
                        background:
                          'linear-gradient(135deg, #2079c2ff 0%, #1f2c33ff 100%)',
                        px: 2,
                        py: 0.5,
                        borderRadius: '18px',
                        boxShadow: '0 2px 10px rgba(48, 71, 83, 0.4)',
                        whiteSpace: 'nowrap',
                        textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                        letterSpacing: 0.4,
                        userSelect: 'none',
                        width: 'fit-content',
                      }}
                    >
                      🗓
                      <span>
                        {formatTimeToAMPM(post.startTime)} •{' '}
                        {formatDateWithSuffix(post.selectedDate)} •{' '}
                        {formatDuration(post.duration)}
                      </span>
                    </Typography>
                  </Box>
                )}

                {/* Status Badge */}
                <Box
                  sx={{
                    mt: 2,
                    display: 'inline-block',
                    backgroundColor: statusColor,
                    color: '#fff',
                    px: 1.5,
                    py: 0.4,
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {status}
                </Box>
              </CardContent>
            </Link>

            {/* Action Buttons */}
            <Box px={2} pb={2}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: '#2196f3',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#2196f3',
                    },
                  }}
                  onClick={() =>
                    navigate(`/my-post-tab-details/${post.id}`)
                  }
                >
                  View
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  disabled={
                    status === 'Completed' 
                  }
                  sx={{
                    borderColor: '#2196f3',
                    color:
                      status === 'Completed' ? '#aaa' : '#2196f3',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor:
                        status === 'Completed'
                          ? 'inherit'
                          : 'rgba(0,188,212,0.1)',
                      borderColor:
                        status === 'Completed'
                          ? '#aaa'
                          : '#2196f3',
                    },
                  }}
                  onClick={() => {
  if (status !== 'Completed') {
    const slug = post.title
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "session";

    navigate(
      `/prejoin?role=host&channel=${slug}&host_name=${encodeURIComponent(
        post.host_name || post.created_by_name || "Host"
      )}&post_id=${post.id}`
    );
  }
}}
                >
                  Join Now
                </Button>
              </Box>
            </Box>
          </Card>
        </Box>
      );
    })}
</Box>


          {/* Pagination Controls */}
          {Array.isArray(posts) && totalPages > 1 && (
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>

              <Typography sx={{ pt: '8px', color: '#ccc' }}>
                Page {page} of {totalPages}
              </Typography>

              <Button
                variant="outlined"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </Box>
          )}
        </Container>
      </Box>
      <Footer2 />
    </>
  );
};

export default MyPosts;
